﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The page used to register additional information about a user account, such as locale, email, phone nr,
// location and "rådgivarkod".

// Can be tested in iRåd, sRåd and hRåd.
// Login to the account and click on the "pen" icon on the Home/Start page (for iRåd this is hidden and
// you have to manually navigate to the url).

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import Grid from "~base/Components/Layout/Grid";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import classnames from "classnames";
import { reducer, RegisterPageState, RegisterPageViewAction } from "./Store";
import Form from "~base/Components/Primitives/Form";
import SplashPage from "~base/Pages/Shared/SplashPage";
import SplashContent from "~base/Pages/Shared/SplashContent";
import { Button } from "~base/Components/Atoms/Button";
import { useIntl } from "~base/Internationalization/IntlContext";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import {
    useNavigateService,
    getSameSiteSafeUrlParam,
} from "~base/Services/NavigateService";
import { useEffect } from "react";
import {
    RegisterPageCustomizations,
    RegisterPageDefaultCustomization,
} from "./Customization";
import { PoweredByConsilio } from "~base/Components/Composite/PoweredByConsilio";
import UserInfoCard from "~base/Pages/Shared/UserInfoCard";
import TitleText from "~base/Components/Primitives/TitleText";
import TitlePhone from "~base/Components/Primitives/TitlePhone";
import TitleEmail from "~base/Components/Primitives/TitleEmail";
import LabelCheckbox from "~base/Components/Primitives/LabelCheckbox";
import TitleIconButton from "~base/Components/Primitives/TitleIconButton";
import RadioButton from "~base/Components/Primitives/RadioButton";
import { useGetAccountRegister } from "~base/Services/API/Account/GetRegister";
import { usePostAccountRegister } from "~base/Services/API/Account/PostRegister";
import * as accountModel from "~base/Services/API/Account/Model";
import { getErrorMessage } from "~base/Services/API";
import { useSettings } from "~base/Settings/SettingsContext";
import { TitleSelect } from "~base/Components/Primitives/TitleSelect";
import { localeSelectValues } from "~base/Pages/Shared/LocalePicker";
import TextBlock from "~base/Components/Primitives/TextBlock";
import { RegisterPageClasses, useAdditionalStyles } from "./Styles";
import { useSearchParams } from "react-router-dom";
import * as phone from "~base/Types/Phone";
import * as email from "~base/Types/Email";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type StartProps = {
    customizations?: RegisterPageCustomizations;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const RegisterPage: React.FunctionComponent<StartProps> = (
    props: StartProps
) => {
    const customization =
        props.customizations ?? RegisterPageDefaultCustomization;
    // Init hooks
    const navigate = useNavigateService();
    const [searchParams] = useSearchParams();
    const intl = useIntl();
    const settings = useSettings();
    const defaultLanguage = settings.valueAsString("account.defaultLanguage");

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("RegisterPage"),
        reducer,
        { loading: true }
    );
    const stateUiCulture = state.uiCulture ?? defaultLanguage;

    const classes: RegisterPageClasses = useAdditionalStyles(customization);

    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const returnUrl =
        getSameSiteSafeUrlParam(searchParams, "ReturnUrl") ??
        `${serverVirtualPath}Home/Start`;

    // API hooks

    const callGetRegisterInfo =
        state.registerInfo == null && state.errorMessage == null;

    const [loadingGetRegisterRegisterApi, __] = useGetAccountRegister({
        call: callGetRegisterInfo,
        callback: async (status, response, data) => {
            if (status == "Success") {
                dispatch({
                    type: "initDataLoaded",
                    data: data,
                    defaultLanguage: defaultLanguage,
                });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, response),
                });
            }
        },
    });

    const [loadingPostRegisterInfo, invokePostRegisterApi] =
        usePostAccountRegister({
            callback: async (status, response) => {
                if (status == "Success") {
                    dispatch({ type: "navigate", uri: returnUrl });
                } else {
                    dispatch({
                        type: "fetchError",
                        message: await getErrorMessage(intl, response),
                    });
                }
            },
        });

    const regCulture = state.registerInfo?.PreferredUICulture;
    useEffect(() => {
        if (state.navigateUri) {
            const loadAsExternal = regCulture !== state.uiCulture; // new UI culture requires page refresh.
            navigate.current(state.navigateUri, undefined, loadAsExternal);
        }
    }, [navigate, state.navigateUri, regCulture, state.uiCulture]);

    // Rendering
    const hideLogout = settings.valueAsBool("Account.HideLogout");
    const languages = settings.valueAsStringArray("cultures");
    const showLanguageSelection = languages.length > 1;

    // Setting languages
    const localesFromLanguages = localeSelectValues(
        customization.languageFilterFunction(languages)
    );
    const uiSelectCultures =
        localesFromLanguages.length > 0
            ? localesFromLanguages
            : [defaultLanguage];

    const radgivarkodReadOnly =
        !customization.allowMultipleAdvisorCodes &&
        state?.registerInfo?.RådgivarKoder != null &&
        state?.registerInfo?.RådgivarKoder.length > 0;

    const pageLoading = state.loading || loadingGetRegisterRegisterApi;
    const formLoading = pageLoading || loadingPostRegisterInfo;

    const jssSpacingStyles = useJssSpacingStyles();
    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const formIsValid =
        !formLoading &&
        state.uiCulture != null &&
        (!customization.verksamhetsortObligatory || !!state.verksamhetsort) &&
        (!customization.phoneObligatory ||
            (!!state.phone && phone.validate(intl, state.phone).isValid)) &&
        (!customization.emailObligatory ||
            (!!state.email && email.validate(intl, state.email).isValid)) &&
        (!customization.allowMultipleAdvisorCodes ||
            (state.rådgivarkod &&
                state.rådgivarKoder.length > 0 &&
                state.rådgivarKoder.find(
                    (c) => !customization.validateRådgivarkod(c.rådgivarKod)
                ) === undefined &&
                new Set(state.rådgivarKoder.map((c) => c.rådgivarKod)).size ==
                    state.rådgivarKoder.length)); // check that rådgivarKoder are unique

    const headerContent = state.registerInfo && (
        <UserInfoCard
            customization={customization.userInfoCustomization}
            className={classes.header}
            displayName={state.registerInfo.UserDisplayName}
            role={state.registerInfo.Role}
            editLink={null}
        />
    );

    const footerContent = (
        <Button
            testIdentifier={"RegisterSubmitButton"}
            type="submit"
            className={classnames(formVerticalSpacingClasses, classes.button)}
            disabled={!formIsValid}
        >
            {intl.formatText("VisiFinance.UI.Register.Spara")}
        </Button>
    );

    return (
        <SplashPage isLoading={pageLoading} showSplashScreen={false}>
            <Form
                testIdentifier={"RegisterForm"}
                onSubmit={() =>
                    onFormSubmit(state, dispatch, invokePostRegisterApi)
                }
                isValid={formIsValid}
            >
                <SplashContent
                    header={headerContent}
                    footer={footerContent}
                    constWidth={true}
                    closeRouteUri={hideLogout ? null : returnUrl}
                >
                    {customization.showVerksamhetsort && (
                        <TitleText
                            testIdentifier="VisiFinance.UI.Register.Verksamhetsort"
                            titleTextCode="VisiFinance.UI.Register.Verksamhetsort"
                            setValue={(x) =>
                                dispatch({
                                    type: "setField",
                                    name: "verksamhetsort",
                                    value: x,
                                })
                            }
                            value={state.verksamhetsort}
                            enabled={!formLoading}
                        />
                    )}

                    {customization.showRådgivarkod &&
                    customization.allowMultipleAdvisorCodes
                        ? state.rådgivarKoder?.map((code, i) => (
                              <React.Fragment key={i}>
                                  <Grid
                                      className={classes.gridAlignment}
                                      columns={4}
                                      templateColumns={"50% 10% 20% 20%"}
                                  >
                                      <TitleText
                                          testIdentifier={
                                              i === 0
                                                  ? "VisiFinance.UI.Register.Rådgivarkod"
                                                  : ""
                                          }
                                          titleTextCode={
                                              i === 0
                                                  ? "VisiFinance.UI.Register.Rådgivarkod"
                                                  : ""
                                          }
                                          value={code.rådgivarKod}
                                          setValue={(x) =>
                                              dispatch({
                                                  type: "changeRådgivarKod",
                                                  code: x,
                                                  id: code.id,
                                                  isChosen:
                                                      state.rådgivarkod ===
                                                      code.rådgivarKod,
                                              })
                                          }
                                          enabled={
                                              !formLoading &&
                                              !radgivarkodReadOnly
                                          }
                                          isValid={
                                              customization.validateRådgivarkod
                                          }
                                      />
                                      <RadioButton
                                          checked={
                                              state.rådgivarkod ===
                                              code.rådgivarKod
                                          }
                                          group="rgKod"
                                          onChange={() =>
                                              dispatch({
                                                  type: "setField",
                                                  name: "rådgivarkod",
                                                  value: code.rådgivarKod,
                                              })
                                          }
                                          testIdentifier={"rgKod option" + i}
                                          className={classes.radioPadding}
                                      />
                                      <TitleIconButton
                                          title={i == 0 ? " " : null}
                                          testIdentifier={`Delete`}
                                          testIdentifierIndex={i}
                                          icon={
                                              "Consilio.UI.Icons.FormControlTrashcan"
                                          }
                                          onClick={() => {
                                              dispatch({
                                                  type: "removeRådgivarKod",
                                                  code: code.rådgivarKod,
                                                  id: code.id,
                                              });
                                          }}
                                          styleType={"FormControl"}
                                          enabled={
                                              state.rådgivarKoder.length > 1
                                          }
                                      />
                                      {i === state.rådgivarKoder.length - 1 ? (
                                          <TitleIconButton
                                              title={i == 0 ? " " : null}
                                              testIdentifier={`Add`}
                                              icon={
                                                  "Consilio.UI.Icons.FormControlPlus"
                                              }
                                              onClick={() => {
                                                  dispatch({
                                                      type: "addRådgivarKod",
                                                  });
                                              }}
                                              styleType={"FormControl"}
                                          />
                                      ) : null}
                                  </Grid>
                              </React.Fragment>
                          ))
                        : customization.showRådgivarkod && (
                              <TitleText
                                  testIdentifier="VisiFinance.UI.Register.Rådgivarkod"
                                  titleTextCode="VisiFinance.UI.Register.Rådgivarkod"
                                  value={state.rådgivarkod}
                                  setValue={(x) =>
                                      dispatch({
                                          type: "setField",
                                          name: "rådgivarkod",
                                          value: x,
                                      })
                                  }
                                  enabled={!formLoading && !radgivarkodReadOnly}
                              />
                          )}

                    {customization.showPhoneNumber && (
                        <TitlePhone
                            testIdentifier="VisiFinance.UI.Register.Telefonnummer"
                            titleTextCode="VisiFinance.UI.Register.Telefonnummer"
                            value={state.phone}
                            setValue={(x) =>
                                dispatch({
                                    type: "setField",
                                    name: "phone",
                                    value: x,
                                })
                            }
                            enabled={!formLoading}
                        />
                    )}

                    {customization.showEmail && (
                        <TitleEmail
                            testIdentifier="VisiFinance.UI.Register.Epost"
                            titleTextCode="VisiFinance.UI.Register.Epost"
                            value={state.email}
                            setValue={(x) =>
                                dispatch({
                                    type: "setField",
                                    name: "email",
                                    value: x,
                                })
                            }
                            enabled={!formLoading}
                        />
                    )}

                    {showLanguageSelection && (
                        <TitleSelect
                            testIdentifier="VisiFinance.UI.Register.Språk"
                            titleTextCode="VisiFinance.UI.Register.Språk"
                            required={true}
                            value={state.uiCulture}
                            stringValues={uiSelectCultures}
                            onChange={(x) =>
                                dispatch({
                                    type: "setField",
                                    name: "uiCulture",
                                    value: x,
                                })
                            }
                        />
                    )}

                    {customization.showArbetarMedDistansrådgivning && (
                        <LabelCheckbox
                            testIdentifier="VisiFinance.UI.Register.ArbetarMedDistansrådgivning"
                            labelTextCode="VisiFinance.UI.Register.ArbetarMedDistansrådgivning"
                            checked={state.arbetarMedDistansrådgivning}
                            onChange={(x) =>
                                dispatch({
                                    type: "setField",
                                    name: "arbetarMedDistansrådgivning",
                                    value: x,
                                })
                            }
                            enabled={!formLoading}
                        />
                    )}

                    {state.errorMessage && (
                        <TextBlock
                            styleType="Paragraph"
                            text={state.errorMessage}
                            styleSubType="Validation"
                        />
                    )}
                </SplashContent>
            </Form>
            <PoweredByConsilio testIdentifier={"PoweredByConsilio"} />
        </SplashPage>
    );
};

const onFormSubmit = (
    state: RegisterPageState,
    dispatch: React.Dispatch<RegisterPageViewAction>,
    invokeSaveAction: (
        data: accountModel.PostRegisterRequest
    ) => Promise<unknown>
) => {
    invokeSaveAction({
        Email: state.email,
        JobbarMedDistansRådgivning: state.arbetarMedDistansrådgivning,
        Phone: state.phone,
        PreferredUICulture: state.uiCulture,
        RådgivarKod: state.rådgivarkod,
        Verksamhetsort: state.verksamhetsort,
        RådgivarKoder: state.rådgivarKoder?.map((x) => {
            return x.rådgivarKod;
        }),
    });
};
